import { useState } from 'react';
import { useSubscription } from '../contexts/SubscriptionContext';

const Paywall = ({ onClose }) => {
    const { packages, purchasePackage, restorePurchases, isPro } = useSubscription();
    const [loadingId, setLoadingId] = useState(null); // Track which button is loading
    const [errorV, setError] = useState(null);

    // Helper to get price string from package
    const getPriceString = (pack) => {
        return pack.product?.priceString
            || pack.rcBillingProduct?.currentPrice?.formattedPrice
            || 'N/A';
    };

    // Helper to determine if annual (check identifier or price > $20)
    const isAnnual = (pack) => {
        const id = pack.identifier?.toLowerCase() || '';
        if (id.includes('annual') || id.includes('yearly') || pack.packageType === 'ANNUAL') {
            return true;
        }
        // Fallback: check if price > $20 (annual plans are typically higher)
        const price = pack.product?.price || pack.rcBillingProduct?.currentPrice?.amountMicros / 1000000 || 0;
        return price > 20;
    };

    const handlePurchase = async (pack) => {
        setLoadingId(pack.identifier);
        setError(null);
        try {
            const success = await purchasePackage(pack);
            if (success) {
                onClose();
            }
        } catch (err) {
            setError(err.message || 'Purchase failed. Please try again.');
        } finally {
            setLoadingId(null);
        }
    };

    const handleRestore = async () => {
        setLoadingId('restore');
        setError(null);
        try {
            await restorePurchases();
            if (isPro) {
                onClose();
            } else {
                setError('No active subscription found to restore.');
            }
        } catch (err) {
            setError(err.message || 'Restore failed');
        } finally {
            setLoadingId(null);
        }
    };

    const isLoading = loadingId !== null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            zIndex: 2000,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            padding: '20px',
        }}>
            <div style={{
                backgroundColor: '#1e293b',
                padding: 'clamp(20px, 5vw, 30px)',
                borderRadius: '20px',
                maxWidth: 'min(400px, calc(100vw - 40px))',
                width: '100%',
                textAlign: 'center',
                position: 'relative',
                boxSizing: 'border-box',
            }}>
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: 'none',
                        border: 'none',
                        color: '#94a3b8',
                        fontSize: '24px',
                        cursor: 'pointer',
                    }}
                >
                    &times;
                </button>

                <h2 style={{ fontSize: '24px', marginBottom: '10px' }}>Unlock Full Access</h2>
                <p style={{ color: '#94a3b8', marginBottom: '30px' }}>
                    Get unlimited access to all characters and advanced features.
                </p>

                {errorV && (
                    <div style={{ color: '#ef4444', marginBottom: '15px' }}>
                        {errorV}
                    </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {packages.map((pack) => {
                        const isThisLoading = loadingId === pack.identifier;
                        const period = isAnnual(pack) ? 'Year' : 'Month';
                        return (
                            <button
                                key={pack.identifier}
                                onClick={() => handlePurchase(pack)}
                                disabled={isLoading}
                                style={{
                                    width: '100%',
                                    padding: '15px',
                                    borderRadius: '12px',
                                    border: 'none',
                                    background: isAnnual(pack)
                                        ? 'linear-gradient(135deg, #22c55e, #3b82f6)'
                                        : 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                                    color: 'white',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    opacity: isLoading ? 0.7 : 1,
                                }}
                            >
                                {isThisLoading ? 'Processing...' : `Subscribe ${getPriceString(pack)} / ${period}`}
                            </button>
                        );
                    })}
                    {packages.length === 0 && (
                        <div style={{ color: '#94a3b8', padding: '20px' }}>
                            Loading subscription options...
                        </div>
                    )}
                </div>

                <button
                    onClick={handleRestore}
                    disabled={isLoading}
                    style={{
                        marginTop: '20px',
                        background: 'none',
                        border: 'none',
                        color: '#94a3b8',
                        fontSize: '14px',
                        textDecoration: 'underline',
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                    }}
                >
                    {loadingId === 'restore' ? 'Restoring...' : 'Restore Purchases'}
                </button>

                <div style={{ marginTop: '30px', fontSize: '12px', color: '#64748b' }}>
                    <a href="https://ripety.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#64748b', marginRight: '10px' }}>Privacy Policy</a>
                    <a href="https://ripety.com/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#64748b' }}>Terms of Service</a>
                </div>
            </div>
        </div>
    );
};

export default Paywall;
